import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
	Box,
	Paper,
	Typography,
	Grid,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Chip,
	CircularProgress,
	InputAdornment,
	Button,
	IconButton,
	Tooltip,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { loadPages } from '../../store/actions';
import { useStyles } from './styles';
import { renderField } from './renderers/fieldRenderers';
import { renderConditionsTable } from './renderers/tableRenderers';
import { renderChartData } from './renderers/chartDataRenderer';
import { renderNestedObject } from './renderers/nestedObjectRenderer';

const PagesDesign = (props) => {
	const classes = useStyles();
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [expandedRow, setExpandedRow] = useState(null);
	const [copiedIndex, setCopiedIndex] = useState(null);
	const copyTimeoutRef = useRef(null);
	const debounceTimeoutRef = useRef(null);

	const handleCopyJSON = (item, index) => {
		const jsonString = JSON.stringify(item, null, 2);
		navigator.clipboard.writeText(jsonString).then(() => {
			setCopiedIndex(index);
			if (copyTimeoutRef.current) {
				clearTimeout(copyTimeoutRef.current);
			}
			copyTimeoutRef.current = setTimeout(() => setCopiedIndex(null), 2000);
		}).catch((err) => {
			console.error('Failed to copy JSON:', err);
		});
	};
	
	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) {
				clearTimeout(copyTimeoutRef.current);
			}
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, []);

	// Debounce search term to avoid expensive operations on every keystroke
	useEffect(() => {
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}
		
		debounceTimeoutRef.current = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setPage(0); // Reset to first page when search changes
		}, 300); // 300ms debounce delay

		return () => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, [searchTerm]);

	const getOrgId = () => {
		const user = JSON.parse(localStorage.getItem('jwtuserData') || 'null');
		return user?.orgId;
	};

	// Fetch pages from API on component mount
	useEffect(() => {
		const appId = props.match.params.appid;
		const orgId = getOrgId();
		if (appId && orgId) {
			props.loadPages({ appId, orgId });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Use pages from Redux store instead of static JSON
	const pagesDesignData = Array.isArray(props.pages) ? props.pages : [];

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleExpandRow = (rowIndex) => {
		setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
	};

	// Optimized search function - searches only specific fields instead of stringifying entire object
	const searchInItem = useCallback((item, searchLower) => {
		// Search in common string fields
		const searchableFields = [
			item.title,
			item.name,
			item.url,
			item.description,
			item.type
		].filter(Boolean).map(field => String(field).toLowerCase());
		
		// Check if search term matches any field
		return searchableFields.some(field => field.includes(searchLower));
	}, []);

	// Filter and search functionality - OPTIMIZED: No more expensive JSON.stringify
	const filteredData = useMemo(() => {
		if (!pagesDesignData || !Array.isArray(pagesDesignData)) {
			return [];
		}

		if (!debouncedSearchTerm) {
			return pagesDesignData;
		}

		const searchLower = debouncedSearchTerm.toLowerCase();
		return pagesDesignData.filter((item) => {
			return searchInItem(item, searchLower);
		});
	}, [debouncedSearchTerm, pagesDesignData, searchInItem]);

	// Paginated data
	const paginatedData = useMemo(() => {
		const startIndex = page * rowsPerPage;
		return filteredData.slice(startIndex, startIndex + rowsPerPage);
	}, [filteredData, page, rowsPerPage]);

	// Memoize active pages count to avoid recalculating on every render
	const activePagesCount = useMemo(() => {
		return pagesDesignData.filter((item) => item.isActive).length;
	}, [pagesDesignData]);

	if (props.pageLoading || !pagesDesignData) {
		return (
			<Box className={classes.root} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box className={classes.root}>
			<Paper className={classes.statsBox}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={4}>
						<Typography variant="h6">Total Pages</Typography>
						<Typography variant="h4">{pagesDesignData.length}</Typography>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Typography variant="h6">Filtered Results</Typography>
						<Typography variant="h4">{filteredData.length}</Typography>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Typography variant="h6">Active Pages</Typography>
						<Typography variant="h4">{activePagesCount}</Typography>
					</Grid>
				</Grid>
			</Paper>

			<TextField
				className={classes.searchBox}
				fullWidth
				variant="outlined"
				placeholder="Search pages by title, name, URL, description..."
				value={searchTerm}
				onChange={(e) => {
					setSearchTerm(e.target.value);
					// Page reset is handled by debounce effect
				}}
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon />
						</InputAdornment>
					),
				}}
			/>

			<TableContainer component={Paper} className={classes.tableContainer}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>#</TableCell>
							<TableCell>Title</TableCell>
							<TableCell>Name</TableCell>
							<TableCell>URL</TableCell>
							<TableCell>Type</TableCell>
							<TableCell>Rank</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{paginatedData.length === 0 ? (
							<TableRow>
								<TableCell colSpan={8} align="center">
									<Typography variant="body2" color="textSecondary">
										{searchTerm ? 'No results found for your search.' : 'No data available.'}
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							paginatedData.map((item, index) => {
								const actualIndex = page * rowsPerPage + index;
								const isExpanded = expandedRow === actualIndex;

								return (
									<React.Fragment key={actualIndex}>
										<TableRow hover>
											<TableCell>{actualIndex + 1}</TableCell>
											<TableCell>{item.title || '-'}</TableCell>
											<TableCell>{item.name || '-'}</TableCell>
											<TableCell>{item.url || '-'}</TableCell>
											<TableCell>{item.type || '-'}</TableCell>
											<TableCell>{item.rank || '-'}</TableCell>
											<TableCell>
												<Chip
													label={item.isActive ? 'Active' : 'Inactive'}
													color={item.isActive ? 'primary' : 'default'}
													size="small"
													className={classes.chip}
												/>
												{item.hide && (
													<Chip label="Hidden" color="secondary" size="small" className={classes.chip} />
												)}
											</TableCell>
											<TableCell>
												<Chip
													label={isExpanded ? 'Hide Details' : 'Show Details'}
													onClick={() => handleExpandRow(actualIndex)}
													clickable
													color={isExpanded ? 'secondary' : 'default'}
													size="small"
												/>
											</TableCell>
										</TableRow>
										{isExpanded && (
											<TableRow>
												<TableCell colSpan={8} style={{ padding: 0 }}>
													<Accordion expanded className={classes.accordionRoot}>
														<AccordionSummary className={classes.accordionSummary}>
															<Typography variant="h6">Page Details</Typography>
														</AccordionSummary>
														<AccordionDetails className={classes.accordionDetails}>
															<TableContainer component={Paper} style={{ marginBottom: 16 }}>
																<Table size="small">
																	<TableHead>
																		<TableRow>
																			<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
																			<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
																		</TableRow>
																	</TableHead>
																	<TableBody>
																		{item.title && (
																			<TableRow>
																				<TableCell>Title:</TableCell>
																				<TableCell>{item.title}</TableCell>
																			</TableRow>
																		)}
																		{item.name && (
																			<TableRow>
																				<TableCell>Name:</TableCell>
																				<TableCell>{item.name}</TableCell>
																			</TableRow>
																		)}
																		{item.url && (
																			<TableRow>
																				<TableCell>URL:</TableCell>
																				<TableCell>{item.url}</TableCell>
																			</TableRow>
																		)}
																		{item.description && (
																			<TableRow>
																				<TableCell>Description:</TableCell>
																				<TableCell>{item.description}</TableCell>
																			</TableRow>
																		)}
																		{item.type && (
																			<TableRow>
																				<TableCell>Type:</TableCell>
																				<TableCell>{item.type}</TableCell>
																			</TableRow>
																		)}
																		{item.rank !== undefined && (
																			<TableRow>
																				<TableCell>Rank:</TableCell>
																				<TableCell>{item.rank}</TableCell>
																			</TableRow>
																		)}
																		{item.isActive !== undefined && (
																			<TableRow>
																				<TableCell>Is Active:</TableCell>
																				<TableCell>
																					{item.isActive ? (
																						<Box display="flex" alignItems="center">
																							<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">true</Typography>
																						</Box>
																					) : (
																						<Box display="flex" alignItems="center">
																							<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">false</Typography>
																						</Box>
																					)}
																				</TableCell>
																			</TableRow>
																		)}
																		{item.isCustom !== undefined && (
																			<TableRow>
																				<TableCell>Is Custom:</TableCell>
																				<TableCell>
																					{item.isCustom ? (
																						<Box display="flex" alignItems="center">
																							<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">true</Typography>
																						</Box>
																					) : (
																						<Box display="flex" alignItems="center">
																							<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">false</Typography>
																						</Box>
																					)}
																				</TableCell>
																			</TableRow>
																		)}
																		{item.hide !== undefined && (
																			<TableRow>
																				<TableCell>Hide:</TableCell>
																				<TableCell>
																					{item.hide ? (
																						<Box display="flex" alignItems="center">
																							<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">true</Typography>
																						</Box>
																					) : (
																						<Box display="flex" alignItems="center">
																							<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">false</Typography>
																						</Box>
																					)}
																				</TableCell>
																			</TableRow>
																		)}
																		{item.isLabel !== undefined && (
																			<TableRow>
																				<TableCell>Is Label:</TableCell>
																				<TableCell>
																					{item.isLabel ? (
																						<Box display="flex" alignItems="center">
																							<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">true</Typography>
																						</Box>
																					) : (
																						<Box display="flex" alignItems="center">
																							<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">false</Typography>
																						</Box>
																					)}
																				</TableCell>
																			</TableRow>
																		)}
																		{item.createdDate && (
																			<TableRow>
																				<TableCell>Created Date:</TableCell>
																				<TableCell>{item.createdDate}</TableCell>
																			</TableRow>
																		)}
																		{item.updatedDate && (
																			<TableRow>
																				<TableCell>Updated Date:</TableCell>
																				<TableCell>{item.updatedDate}</TableCell>
																			</TableRow>
																		)}
																		{item.selectedRoles && item.selectedRoles.length > 0 && (
																			<TableRow>
																				<TableCell>Selected Roles:</TableCell>
																				<TableCell>
																					<Box display="flex" flexWrap="wrap">
																						{item.selectedRoles.map((role, roleIndex) => (
																							<Chip
																								key={roleIndex}
																								label={role.name || role.label || role.value}
																								className={classes.chip}
																								size="small"
																								style={{ marginRight: 8, marginBottom: 8 }}
																							/>
																						))}
																					</Box>
																				</TableCell>
																			</TableRow>
																		)}
																		{item.appId && (
																			<TableRow>
																				<TableCell>App ID:</TableCell>
																				<TableCell>{item.appId}</TableCell>
																			</TableRow>
																		)}
																		{item.labelPage && (
																			<TableRow>
																				<TableCell>Label Page:</TableCell>
																				<TableCell>{item.labelPage}</TableCell>
																			</TableRow>
																		)}
																		{item.hasLabel !== undefined && (
																			<TableRow>
																				<TableCell>Has Label:</TableCell>
																				<TableCell>
																					{item.hasLabel ? (
																						<Box display="flex" alignItems="center">
																							<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">true</Typography>
																						</Box>
																					) : (
																						<Box display="flex" alignItems="center">
																							<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
																							<Typography component="span">false</Typography>
																						</Box>
																					)}
																				</TableCell>
																			</TableRow>
																		)}
																	</TableBody>
																</Table>
															</TableContainer>
															<Grid container spacing={2} style={{ marginTop: 16 }}>
																<Grid item xs={12}>
																	{item.chart && item.chart.length > 0 && (
																		<Box>
																			<Typography variant="h6" gutterBottom>
																				Charts ({item.chart.length})
																			</Typography>
																			{item.chart.map((chart, chartIndex) => (
																				<Box key={chartIndex} mb={3} mt={2} style={{ borderTop: '1px solid #e0e0e0', paddingTop: 16 }}>
																					<Typography variant="h6" style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 16 }}>
																						chart{chartIndex + 1}: {chart.chartType || 'N/A'} (ID: {chart._id || chart.chartUniqueId || 'N/A'})
																					</Typography>
																					{chart.chartData ? (
																						renderChartData(chart.chartData, chart, classes)
																					) : (
																						renderNestedObject(chart, 0, [], classes)
																					)}
																				</Box>
																			))}
																		</Box>
																	)}
																</Grid>
																<Grid item xs={12}>
																	{item.conditions && item.conditions.length > 0 && (
																		<Box>
																			<Typography variant="h6" gutterBottom>
																				Conditions ({item.conditions.length})
																			</Typography>
																			{renderConditionsTable(item.conditions)}
																		</Box>
																	)}
																</Grid>
																<Grid item xs={12}>
																	<Box>
																		<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
																			<Typography variant="h6">
																				Full JSON
																			</Typography>
																			<Tooltip title={copiedIndex === actualIndex ? 'Copied!' : 'Copy JSON'}>
																				<IconButton
																					size="small"
																					onClick={() => handleCopyJSON(item, actualIndex)}
																					style={{ marginLeft: 8 }}
																				>
																					<FileCopyIcon fontSize="small" />
																				</IconButton>
																			</Tooltip>
																		</Box>
																		<Box className={classes.jsonView}>
																			<pre>{JSON.stringify(item, null, 2)}</pre>
																		</Box>
																	</Box>
																</Grid>
															</Grid>
														</AccordionDetails>
													</Accordion>
												</TableCell>
											</TableRow>
										)}
									</React.Fragment>
								);
							})
						)}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					count={filteredData.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onChangePage={handleChangePage}
					onChangeRowsPerPage={handleChangeRowsPerPage}
					rowsPerPageOptions={[5, 10, 25, 50, 100]}
				/>
			</TableContainer>
		</Box>
	);
};

const mapStateToProps = (state) => ({
	user: state.user,
	pages: state.pages.pages,
	pageLoading: state.pages.loading,
	pageError: state.pages.error,
});

const mapDispatchToProps = {
	loadPages,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PagesDesign));
